import api from './api';

/**
 * Program Service
 * Handles program information and statistics
 */

/**
 * Get program information
 * @param {string} programType - Program type (si, ta, la)
 */
export const getProgramInfo = async (programType) => {
    const response = await api.get(`/program/${programType}`);
    return response.data;
};

/**
 * Get program timeline
 * @param {string} programType - Program type (si, ta, la)
 * @param {string} term - Academic term
 */
export const getProgramTimeline = async (programType, term) => {
    const params = term ? { term } : {};
    const response = await api.get(`/program/${programType}/timeline`, {
        params,
    });
    return response.data;
};

/**
 * Get SI program statistics (coordinator only)
 * @param {string} term - Optional term filter
 */
export const getSIStats = async (term) => {
    const params = term ? { term } : {};
    const response = await api.get('/program/si/stats', { params });
    return response.data;
};

/**
 * Get TA program statistics (coordinator only)
 * @param {string} term - Optional term filter
 */
export const getTAStats = async (term) => {
    const params = term ? { term } : {};
    const response = await api.get('/program/ta/stats', { params });
    return response.data;
};

/**
 * Get LA program statistics (coordinator only)
 * @param {string} term - Optional term filter
 */
export const getLAStats = async (term) => {
    const params = term ? { term } : {};
    const response = await api.get('/program/la/stats', { params });
    return response.data;
};

/**
 * A program's term at a glance, for its coordinators: what needs attention,
 * hiring progress and where the requests stand, as counts only
 * @param {string} programType - Program type (si, ta, la)
 * @param {string} term - Term code
 */
export const getOverview = async (programType, term) => {
    const response = await api.get(`/program/${programType}/overview`, {
        params: { term },
    });
    return response.data;
};

/**
 * Get combined stats for all programs (admin only)
 * @param {string} term - Optional term filter
 */
export const getAllStats = async (term) => {
    const params = term ? { term } : {};
    const response = await api.get('/program/stats', { params });
    return response.data;
};

/**
 * Get all program configurations (coordinator/admin)
 */
export const getAllConfigs = async () => {
    const response = await api.get('/program/config');
    return response.data;
};

/**
 * Get config for a specific program
 * @param {string} type - Program type (si, ta, la)
 */
export const getConfig = async (type) => {
    const response = await api.get(`/program/config/${type}`);
    return response.data;
};

/**
 * Create or update program configuration (coordinator/admin)
 * @param {string} type - Program type (si, ta, la)
 * @param {object} configData - Configuration data
 */
export const upsertConfig = async (type, configData) => {
    const response = await api.put(`/program/config/${type}`, configData);
    return response.data;
};

/**
 * The events a program can set notifications for, with each recipient role,
 * its default, and which events are required (locked on). Coordinators and
 * admins only.
 * @returns {Promise<{ events: { key: string, label: string, required: boolean,
 *           roles: { role: string, label: string, defaultMode: string }[] }[] }>}
 */
export const getNotificationEvents = async () => {
    const response = await api.get('/program/notification-events');
    return response.data;
};

/**
 * Get eligible programs for a specific course (GAP-12)
 * @param {string} courseId - Course ID to check eligibility for
 */
export const getEligiblePrograms = async (courseId) => {
    const response = await api.get(`/program/eligible/${courseId}`);
    return response.data;
};

/**
 * What a program asks of faculty and of the student in the role (#404)
 * @param {string} programType - Program type (si, ta, la)
 * @param {string} term - Term code, for the training sessions
 */
export const getProgramRequirements = async (programType, term) => {
    const params = term ? { term } : {};
    const response = await api.get(`/program/${programType}/requirements`, {
        params,
    });
    return response.data;
};

/**
 * Whether each program is switched on this term. Public, so the signed-out
 * pages can read it.
 * @returns {Promise<{ active: { la: boolean, si: boolean, ta: boolean } }>}
 */
export const getProgramAvailability = async () => {
    const response = await api.get('/program/availability');
    return response.data;
};

export default {
    getProgramAvailability,
    getProgramInfo,
    getProgramRequirements,
    getProgramTimeline,
    getSIStats,
    getTAStats,
    getLAStats,
    getOverview,
    getAllStats,
    getAllConfigs,
    getConfig,
    upsertConfig,
    getNotificationEvents,
    getEligiblePrograms,
};
